import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CHANNEL_TYPES, type CordialityChoice, type CordialityReviewDTO } from '@sac/contracts'
import { api } from '../lib/api'
import { qk } from '../lib/query'
import { ChannelMark, channelName } from '../components/icons'

/**
 * Cordialidade — os avisos de tom antes de enviar, para um admin conferir. Só admin: a API
 * devolve 403 para o resto, e esta tela nem é oferecida a quem não é.
 */

const PERIODOS: [string, string][] = [['7', '7 dias'], ['30', '30 dias'], ['90', '90 dias'], ['', 'Tudo']]
const ESCOLHAS: Record<CordialityChoice | 'pending', string> = {
  suggestion: 'Enviou a sugestão',
  edited: 'Editou e enviou',
  original: 'Enviou o original',
  cancelled: 'Voltou sem enviar',
  skipped_error: 'Erro — enviado sem avaliar',
  pending: 'Sem desfecho',
}
const LIMIT = 50

const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '—')
const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

/** Palavras de `b` que não estão na subsequência comum com `a`. LCS simples: textos curtos. */
function changed(a: string, b: string): { w: string; diff: boolean }[] {
  const A = a.split(/(\s+)/)
  const B = b.split(/(\s+)/)
  const L = Array.from({ length: A.length + 1 }, () => new Array<number>(B.length + 1).fill(0))
  for (let i = A.length - 1; i >= 0; i--)
    for (let j = B.length - 1; j >= 0; j--) L[i]![j] = A[i] === B[j] ? L[i + 1]![j + 1]! + 1 : Math.max(L[i + 1]![j]!, L[i]![j + 1]!)
  const out: { w: string; diff: boolean }[] = []
  let i = 0
  let j = 0
  while (j < B.length) {
    if (i < A.length && A[i] === B[j]) { out.push({ w: B[j]!, diff: false }); i++; j++ }
    else if (i < A.length && L[i + 1]![j]! >= L[i]![j + 1]!) i++
    else { out.push({ w: B[j]!, diff: Boolean(B[j]!.trim()) }); j++ }
  }
  return out
}

function Diff({ base, text, kind }: { base: string; text: string; kind: 'add' | 'del' }) {
  return (
    <>
      {changed(base, text).map((t, i) => (t.diff ? <mark key={i} className={`cd-${kind}`}>{t.w}</mark> : t.w))}
    </>
  )
}

function Linha({ r, onOpen }: { r: CordialityReviewDTO; onOpen: () => void }) {
  const enviado = r.finalText
  return (
    <div className="log-row">
      <div className="log-meta">
        <ChannelMark channel={r.channel} size={15} />
        <b>{r.attendantName ?? '—'}</b>
        <span className="t2">{quando(r.createdAt)}</span>
        {r.jevScore !== null && (
          <span className={`log-origin ${r.passed ? 'ia' : 'auto'}`} title={r.jevReasons.join(' · ')}>
            nota {r.jevScore}
          </span>
        )}
        <span className="t2">{ESCOLHAS[r.choice ?? 'pending']}</span>
        {r.jevReasons.length > 0 && <span className="t2">{r.jevReasons.join(' · ')}</span>}
        <span className="log-sp" />
        <button className="btn sec sm" onClick={onOpen}>Abrir conversa</button>
      </div>
      <div className="cd-cols">
        <div>
          <span className="cd-h">Original</span>
          <div className="cd-t">{enviado ? <Diff base={enviado} text={r.originalText} kind="del" /> : r.originalText}</div>
        </div>
        <div>
          <span className="cd-h">Sugerido</span>
          <div className="cd-t">
            {r.suggestedText ? <Diff base={r.originalText} text={r.suggestedText} kind="add" /> : <span className="t2">{r.passed ? 'Passou — sem sugestão' : 'Sem sugestão'}</span>}
          </div>
        </div>
        <div>
          <span className="cd-h">Enviado</span>
          <div className="cd-t">{enviado ? <Diff base={r.originalText} text={enviado} kind="add" /> : <span className="t2">Nada enviado</span>}</div>
        </div>
      </div>
    </div>
  )
}

function csv(rows: CordialityReviewDTO[]) {
  const cols: [string, (r: CordialityReviewDTO) => unknown][] = [
    ['data', (r) => r.createdAt], ['canal', (r) => r.channel], ['atendente', (r) => r.attendantName],
    ['nota', (r) => r.jevScore], ['passou', (r) => r.passed], ['motivos', (r) => r.jevReasons.join(' | ')],
    ['escolha', (r) => r.choice], ['original', (r) => r.originalText], ['sugerido', (r) => r.suggestedText],
    ['enviado', (r) => r.finalText], ['ms_jev', (r) => r.latencyMsJev], ['ms_reescrita', (r) => r.latencyMsRewrite],
    ['conversa', (r) => r.conversationId],
  ]
  const cell = (v: unknown) => (v === null || v === undefined ? '' : `"${String(v).replace(/"/g, '""')}"`)
  const body = [cols.map((c) => c[0]).join(','), ...rows.map((r) => cols.map(([, f]) => cell(f(r))).join(','))].join('\n')
  const a = document.createElement('a')
  // BOM: sem ele o Excel abre acento como lixo.
  a.href = URL.createObjectURL(new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' }))
  a.download = `cordialidade-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(a.href)
}

export function Cordialidade({ onOpenConversation }: { onOpenConversation?: (id: string) => void }) {
  const [days, setDays] = useState('30')
  const [channel, setChannel] = useState('')
  const [attendant, setAttendant] = useState('')
  const [choice, setChoice] = useState('')
  const [page, setPage] = useState(0)
  const troca = (set: (v: string) => void) => (v: string) => { set(v); setPage(0) }

  const { data: agents = [] } = useQuery({ queryKey: qk.agents(), queryFn: api.agents })
  const filtros = { days, channel, attendant, choice }
  const params = { ...filtros, limit: LIMIT, offset: page * LIMIT }
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['cordiality', 'reviews', params],
    queryFn: () => api.cordialityReviews(params),
    placeholderData: (prev) => prev,
  })
  const { data: st } = useQuery({ queryKey: ['cordiality', 'stats', filtros], queryFn: () => api.cordialityStats(filtros) })
  const [exporting, setExporting] = useState(false)

  const desfecho = st ? st.outcomes.suggestion + st.outcomes.edited + st.outcomes.original + st.outcomes.cancelled : 0

  return (
    <div className="crm">
      <div className="crm-head">
        <h2>Cordialidade</h2>
        <p>
          Respostas escritas à mão que o Jev achou secas antes do envio, o que a IA sugeriu e o que de fato saiu. Liga
          e desliga em Configurações → Integrações → Cordialidade.
        </p>
      </div>

      <div className="crm-filters">
        <select value={days} onChange={(e) => troca(setDays)(e.target.value)}>
          {PERIODOS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
        </select>
        <select value={channel} onChange={(e) => troca(setChannel)(e.target.value)}>
          <option value="">Todos os canais</option>
          {CHANNEL_TYPES.map((c) => <option key={c} value={c}>{channelName(c)}</option>)}
        </select>
        <select value={attendant} onChange={(e) => troca(setAttendant)(e.target.value)}>
          <option value="">Qualquer atendente</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={choice} onChange={(e) => troca(setChoice)(e.target.value)}>
          <option value="">Qualquer desfecho</option>
          {Object.entries(ESCOLHAS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button
          className="btn sec sm"
          disabled={exporting}
          onClick={async () => {
            setExporting(true)
            try { csv(await api.cordialityReviews({ ...filtros, limit: 2000 })) } finally { setExporting(false) }
          }}
        >
          {exporting ? 'Exportando…' : 'Exportar CSV'}
        </button>
      </div>

      {st && (
        <div className="cd-stats">
          <div><b>{st.checks}</b><span>avaliações</span></div>
          <div><b>{pct(st.failed, st.checks)}</b><span>pareceram secas</span></div>
          <div><b>{pct(st.outcomes.suggestion, desfecho)}</b><span>sugestão aceita</span></div>
          <div><b>{pct(st.outcomes.edited, desfecho)}</b><span>editada</span></div>
          <div><b>{pct(st.outcomes.original + st.outcomes.cancelled, desfecho)}</b><span>ignorada</span></div>
          <div><b>{st.outcomes.skipped_error}</b><span>erros (enviadas sem avaliar)</span></div>
        </div>
      )}
      {st && st.weekly.length > 0 && (
        <table className="cd-week">
          <thead><tr><th>Semana</th><th>Atendente</th><th>Nota média</th><th>Avaliações</th></tr></thead>
          <tbody>
            {st.weekly.map((w) => (
              <tr key={w.week + w.attendantId}>
                <td>{w.week.split('-').reverse().join('/')}</td><td>{w.attendantName ?? '—'}</td><td>{w.avgScore}</td><td>{w.checks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isLoading ? (
        <div className="empty">Carregando…</div>
      ) : !rows.length ? (
        <div className="empty" style={{ padding: 28 }}>Nenhuma avaliação com esses filtros.</div>
      ) : (
        <div className="log-list">
          {rows.map((r) => <Linha key={r.id} r={r} onOpen={() => onOpenConversation?.(r.conversationId)} />)}
        </div>
      )}

      <div className="ord-pager">
        <button className="btn sec sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</button>
        <span className="cfg-note">página {page + 1}</span>
        <button className="btn sec sm" disabled={rows.length < LIMIT} onClick={() => setPage((p) => p + 1)}>Próxima</button>
      </div>
    </div>
  )
}
